<script setup lang="ts">
import { ref, computed } from 'vue'
import type { Team, Personnel } from '@/types'
import { TeamStatus, MedicalQual } from '@/types'

import TeamTooltip from '@/components/teams/TeamTooltip.vue'
import { usePersonnelStore } from '@/stores/personnel'

const props = defineProps<{
  team: Team
  hasDebriefable: boolean
}>()

const emit = defineEmits<{
  dragstart: [teamId: string]
  debrief: []
  'view-details': []
}>()


const dragStartX = ref(0)
const dragStartY = ref(0)

function onMouseDown(e: MouseEvent) {
  dragStartX.value = e.clientX
  dragStartY.value = e.clientY
}

function onMouseUp(e: MouseEvent) {
  const dx = Math.abs(e.clientX - dragStartX.value)
  const dy = Math.abs(e.clientY - dragStartY.value)
  if (dx < 5 && dy < 5) {
    showDetails.value = true
  }
}

const showDetails = ref(false)

const statusDot: Record<TeamStatus, string> = {
  [TeamStatus.Building]: 'bg-amber-500',
  [TeamStatus.AwaitingAssignment]: 'bg-violet-500',
  [TeamStatus.Assigned]: 'bg-blue-500',
  [TeamStatus.Enroute]: 'bg-teal-500',
  [TeamStatus.OnScene]: 'bg-green-500',
  [TeamStatus.RTB]: 'bg-amber-500',
  [TeamStatus.Returned]: 'bg-slate-400',
  [TeamStatus.Emergency]: 'bg-red-500',
}

const statusText: Record<TeamStatus, string> = {
  [TeamStatus.Building]: 'text-amber-500',
  [TeamStatus.AwaitingAssignment]: 'text-violet-500',
  [TeamStatus.Assigned]: 'text-blue-500',
  [TeamStatus.Enroute]: 'text-teal-500',
  [TeamStatus.OnScene]: 'text-green-500',
  [TeamStatus.RTB]: 'text-amber-500',
  [TeamStatus.Returned]: 'text-slate-400',
  [TeamStatus.Emergency]: 'text-red-500',
}

const statusLabel: Record<TeamStatus, string> = {
  [TeamStatus.Building]: 'Building',
  [TeamStatus.AwaitingAssignment]: 'Awaiting',
  [TeamStatus.Assigned]: 'Assigned',
  [TeamStatus.Enroute]: 'En Route',
  [TeamStatus.OnScene]: 'In Assignment',
  [TeamStatus.RTB]: 'RTB',
  [TeamStatus.Returned]: 'Returned',
  [TeamStatus.Emergency]: 'Emergency',
}

const personnelStore = usePersonnelStore()

const teamMembers = computed(() =>
  personnelStore.roster
    .filter(p => p.team_id === props.team.team_id && p.time_out === null)
    .sort((a, b) => {
      if (a.personnel_id === props.team.team_leader_personnel_id) return -1
      if (b.personnel_id === props.team.team_leader_personnel_id) return 1
      return 0
    })
)
const teamMemberCount = computed(() =>
  teamMembers.value.length
)

const hasTeamMedic = computed(() =>
  teamMembers.value.some(isMedic)
)

const teamAgencyLabel = computed(() => {
  if (teamMembers.value.length === 0) return null
  const agencies = [...new Set(teamMembers.value.map(p => p.agency).filter(Boolean))]
  if (agencies.length === 1) return agencies[0]
  return `${agencies.length} agencies`
})

const EMT_AND_ABOVE = new Set<string>([MedicalQual.EMT, MedicalQual.NurseParamedic, MedicalQual.MD])

function isMedic(person: Personnel): boolean {
  return EMT_AND_ABOVE.has((person.qualifications?.medical_qual ?? '') as string)
}

function getQualChips(person: Personnel): { label: string; highlight: boolean }[] {
  const q = person.qualifications ?? {}
  const chips: { label: string; highlight: boolean }[] = []
  if (q.medical_qual && q.medical_qual !== 'None') {
    chips.push({ label: q.medical_qual as string, highlight: EMT_AND_ABOVE.has(q.medical_qual as string) })
  }
  if (q.searcher_type && q.searcher_type !== 'None') chips.push({ label: q.searcher_type as string, highlight: false })
  if (q.rope_qual && q.rope_qual !== 'None') chips.push({ label: q.rope_qual as string, highlight: false })
  if (Array.isArray(q.misc_quals)) {
    chips.push(...(q.misc_quals as string[]).map(l => ({ label: l, highlight: false })))
  }
  return chips
}

</script>

<template>
  <div
    class="@container bg-surface-container p-3 pt-2 select-none rounded-md cursor-grab active:cursor-grabbing"
    @mousedown="onMouseDown"
    @mouseup="onMouseUp"
  >
    <TeamTooltip :team="team" side="right">
      <!-- Top row: status dot + name (left) · type + member count (right) -->
      <div class="flex items-center justify-between gap-2">
        <div class="flex items-center gap-2 min-w-0">
          <div class="w-2 h-2 shrink-0 rounded-full @[380px]:hidden" :class="statusDot[team.team_status]"></div>
          <span class="hidden @[380px]:inline text-[10px] font-mono uppercase tracking-widest shrink-0" :class="statusText[team.team_status]">{{ statusLabel[team.team_status] }}</span>
          <p class="text-sm font-medium text-on-surface truncate">{{ team.team_identifier }}</p>
          <span v-if="hasTeamMedic" class="material-symbols-outlined !text-[12px] text-green-600">medical_services</span>
          <span v-if="teamAgencyLabel" class="hidden @[200px]:inline text-[10px] font-mono text-outline truncate">{{ teamAgencyLabel }}</span>
        </div>
        <div class="shrink-0 flex items-center gap-1.5">
          <span class="hidden @[220px]:inline text-[10px] font-label uppercase tracking-widest text-outline">{{ team.team_type }}</span>
          <span class="flex items-center gap-0.5 text-[10px] font-mono text-outline-variant">
            <span class="material-symbols-outlined !text-[12px]">person</span>
            <span>{{ teamMemberCount }}</span>
          </span>
        </div>
      </div>

      <!-- Member list: visible at @[280px]+ -->
      <div v-if="teamMembers.length > 0" class="hidden @[285px]:flex flex-col gap-1 mt-1.5">
        <div
          v-for="member in teamMembers"
          :key="member.personnel_id"
          class="flex flex-col gap-0 min-w-0"
        >
          <div class="flex items-center gap-1 min-w-0">
            <span class="font-bold text-[11px] text-on-surface-variant truncate leading-tight">
              {{ member.first }} {{ member.last }}
            </span>
            <span v-if="team.team_leader_personnel_id === member.personnel_id" class="material-symbols-outlined !text-[11px] text-amber-500 shrink-0" title="Team leader">star</span>
            <span v-if="isMedic(member)" class="@[380px]:!hidden material-symbols-outlined !text-[11px] text-green-600 shrink-0" title="EMT or above">medical_services</span>
            <span v-if="member.agency" class="text-[9px] font-mono text-outline-variant truncate">{{ member.agency }}</span>
          </div>
          <div class="hidden @[380px]:flex flex-wrap gap-1 mt-0.5">
            <span
              v-for="chip in getQualChips(member)"
              :key="chip.label"
              class="inline-flex items-center px-1 py-0.3 text-[9px] font-mono border rounded"
              :class="chip.highlight
                ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-500 font-semibold'
                : 'bg-surface-container border-border text-on-surface/50'"
            >
              {{ chip.label }}
            </span>
          </div>
        </div>
      </div>
    </TeamTooltip>
  </div>
</template>
